import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { Box, Button, Menu, MenuItem, useMediaQuery } from '@mui/material'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

// others
import { useDebounce } from 'use-debounce'

// components
import dayjs from 'dayjs'
import { useSearchParams } from 'react-router-dom'
import { LedgerReportPayload, ReportFileType, useGenerateGeneralLedgerLazyQuery } from '../../../../generated'
import { exportLedger } from '../../../../services'
import { FILTER_DATE_FORMAT, GRAPHQL_QUERY_POLICY, PAGE_LIMIT, QUERY_PARAMS } from '../../../constants'
import { AppContext } from '../../../context'
import { ActionType } from '../../../context/AppContextReducer'
import { CardBox } from '../../../theme/styleComponents'
import { anchorOrigin, flexCenter, transformOrigin } from '../../../theme/styleConstants'
import { Alert } from '../../common/Alert'
import { AccountsDropdown } from '../../common/ChartOfAccountsDropdown'
import { ContentHead } from '../../common/ContentHead'
import { EmptyData } from '../../common/EmptyData'
import { SearchWithKeypress } from '../../common/SearchWithKeypress'
import { DateRanges } from '../../entries/EntryFilters'
import LedgerListing from './LedgerListing'

const GeneralLedger = () => {
  const [ledgerRecords, setLedgerRecords] = useState<LedgerReportPayload['reports']>([])
  const [exporting, setExporting] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const fromParam =
    searchParams.get(QUERY_PARAMS.FROM) ?? dayjs(new Date()).subtract(1, 'month').format(FILTER_DATE_FORMAT)
  const toParam = searchParams.get(QUERY_PARAMS.TO) ?? dayjs().add(1, 'day').format(FILTER_DATE_FORMAT)
  const accountIdParam = searchParams.get(QUERY_PARAMS.ACCOUNT_ID)
  const mediumViewport = useMediaQuery('(min-width:929px)')

  const fromDate = useMemo(
    () =>
      fromParam
        ? dayjs(fromParam!).format(FILTER_DATE_FORMAT)
        : dayjs(new Date()).subtract(1, 'month').format(FILTER_DATE_FORMAT),
    [fromParam]
  )
  const toDate = useMemo(
    () => (toParam ? dayjs(toParam!).format(FILTER_DATE_FORMAT) : dayjs().add(1, 'day').format(FILTER_DATE_FORMAT)),
    [toParam]
  )

  const { searchValue, currentPage, dispatch } = useContext(AppContext)
  const [debouncedSearch] = useDebounce(searchValue, 500)

  const getFilterValues = (key: QUERY_PARAMS) => searchParams.get(key) || QUERY_PARAMS.ALL

  const setParams = (key: QUERY_PARAMS, value: string) => {
    switch (key) {
      case QUERY_PARAMS.FROM:
        searchParams.set(key, value)
        break
      case QUERY_PARAMS.TO:
        searchParams.set(key, value)
        break
      case QUERY_PARAMS.ACCOUNT_ID:
        searchParams.set(key, value)
        break
      default:
        searchParams.delete(QUERY_PARAMS.FROM)
        searchParams.delete(QUERY_PARAMS.TO)
        searchParams.delete(QUERY_PARAMS.ACCOUNT_ID)
        break
    }
    setSearchParams(searchParams)
  }

  const exportGL = useCallback(
    async (type: ReportFileType) => {
      setExporting(true)
      try {
        await exportLedger({
          from: dayjs(fromDate).format('YYYY-MM-DD'),
          to: dayjs(toDate).format('YYYY-MM-DD'),
          type,
          accountId: accountIdParam ?? undefined
        })
      } catch (error) {
        console.log(error)
        if (error instanceof Error) Alert.error('Error: ' + error?.message)
      } finally {
        setExporting(false)
      }
    },
    [fromDate, toDate, accountIdParam]
  )

  const [fetchLedger, { loading }] = useGenerateGeneralLedgerLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError(error) {
      Alert.error(error.message)
    },
    onCompleted(data) {
      const { generateGeneralLedger } = data || {}
      const { reports, pagination } = generateGeneralLedger || {}
      setLedgerRecords(reports as LedgerReportPayload['reports'])
      if (pagination) {
        const { totalPages, totalCount } = pagination

        dispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: totalPages || 1 })
        dispatch({ type: ActionType.SET_TOTAL_DATA_COUNT, totalDataCount: totalCount || 1 })
      }
    }
  })

  useEffect(() => {
    fetchLedger({
      variables: {
        input: {
          paginationOptions: {
            page: currentPage,
            limit: PAGE_LIMIT
          },
          keyword: debouncedSearch,
          startDate: fromParam,
          endDate: toParam,
          accountId: accountIdParam ?? undefined
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch, fromDate, toDate, accountIdParam])

  useEffect(() => {
    return () => {
      dispatch({ type: ActionType.SET_SEARCH_VALUE, searchValue: '' })
      return dispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    }
  }, [dispatch])

  const isEmpty = !loading && ledgerRecords?.length === 0

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  return (
    <CardBox marginTop={3}>
      <ContentHead
        title={'General Ledger'}
        customButton={() => (
          <Button
            variant="contained"
            // endIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <GetAppIcon />}
            endIcon={<ArrowDropDownIcon />}
            disabled={exporting}
            onClick={handleMenuClick}
          >
            Export
          </Button>
        )}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2
        }}
      >
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
          PaperProps={{
            sx: { minWidth: '120px' }
          }}
        >
          <MenuItem onClick={() => exportGL(ReportFileType.Csv)}>CSV</MenuItem>

          <MenuItem onClick={() => exportGL(ReportFileType.Pdf)}>PDF</MenuItem>
        </Menu>
      </Box>

      <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} mx={2} mb={3}>
        <Box display={'flex'} gap={2} my={2}>
          <AccountsDropdown
            label="Accounts"
            setParams={setParams}
            accountId={getFilterValues(QUERY_PARAMS.ACCOUNT_ID)}
          />

          <DateRanges
            setParams={setParams}
            getFilterValues={getFilterValues}
            mediumViewport={mediumViewport}
            smallTextField={false}
          />
        </Box>

        <SearchWithKeypress searchPlaceholder={'Search Ledger'} />
      </Box>

      {isEmpty ? (
        <Box sx={{ ...flexCenter, height: 'calc(100vh - 400px)' }}>
          <EmptyData title={'No Records!'} />
        </Box>
      ) : (
        <LedgerListing loading={loading} reports={ledgerRecords} />
      )}
    </CardBox>
  )
}

export default GeneralLedger
